import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { messagesService, parseCreateThreadError, } from "@/api/services/messages.service";
import { useAuth } from "@/context/AuthContext";
import { getUserFullName } from "@/utils/formatDisplayName";
import { useToast } from "@/components/common";
import { useMessagesSignalR } from "@/hooks/useMessagesSignalR";
import { formatRelativeTime, formatDateTimeAUS } from "@/utils/dateTime";
import styles from "./JobNotificationsTab.module.css";
export const JobNotificationsTab = ({ jobId, contactId, jobNumber, onMarkedAsRead, complaintNotes = null, complaintDate = null, }) => {
    const { user } = useAuth();
    const { error: showError, info: showInfo } = useToast();
    const [thread, setThread] = useState(null);
    const [selectedThreadId, setSelectedThreadId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [error, setError] = useState(null);
    const [sending, setSending] = useState(false);
    const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(false);
    const loadJobConversation = useCallback(async () => {
        if (!jobId || !contactId)
            return;
        setLoading(true);
        setError(null);
        try {
            let result = await messagesService.getThreadByJob(jobId, contactId);
            if (!result) {
                try {
                    const createRes = await messagesService.createThread({
                        contactId,
                        type: "JOB",
                        jobId,
                    });
                    const thread = createRes.data;
                    if (thread?.id) {
                        result = {
                            thread: thread,
                            messages: [],
                        };
                    }
                }
                catch (createErr) {
                    const { message } = parseCreateThreadError(createErr);
                    setError(message);
                    showError(message);
                }
            }
            if (result) {
                setThread(result.thread);
                setSelectedThreadId(result.thread.id);
                setMessages(result.messages);
                messagesService.markAsRead(result.thread.id).then(() => onMarkedAsRead?.()).catch(() => { });
            }
            else {
                setThread(null);
                setSelectedThreadId(null);
                setMessages([]);
            }
        }
        catch (err) {
            console.error("Failed to load job conversation:", err);
            setError("Failed to load messages");
            showError("Failed to load messages");
        }
        finally {
            setLoading(false);
            setLoadingMessages(false);
        }
    }, [jobId, contactId, showError]);
    const refetchMessages = useCallback(async () => {
        if (!jobId || !contactId || !selectedThreadId)
            return;
        setLoadingMessages(true);
        try {
            const result = await messagesService.getThreadByJob(jobId, contactId);
            if (result && result.thread.id === selectedThreadId) {
                setThread(result.thread);
                setMessages(result.messages);
            }
        }
        catch (err) {
            console.error("Failed to refetch messages:", err);
        }
        finally {
            setLoadingMessages(false);
        }
    }, [jobId, contactId, selectedThreadId]);
    useEffect(() => {
        loadJobConversation();
    }, [loadJobConversation]);
    useMessagesSignalR(selectedThreadId, {
        onNewMessage: () => {
            refetchMessages();
            showInfo("New message received");
        },
    });
    const handleSendMessage = async () => {
        if (!messageText.trim())
            return;
        setSending(true);
        try {
            if (selectedThreadId) {
                await messagesService.sendMessage({
                    threadId: selectedThreadId,
                    content: messageText.trim(),
                    assignedcontactId: contactId,
                    contactId,
                    senderId: user?.id,
                    senderName: getUserFullName(user, "Staff"),
                });
                setMessageText("");
                await refetchMessages();
            }
            else {
                const response = await messagesService.sendMessage({
                    content: messageText.trim(),
                    threadType: "job",
                    jobId,
                    contactId,
                    assignedcontactId: contactId,
                    senderId: user?.id,
                    senderName: getUserFullName(user, "Staff"),
                });
                setMessageText("");
                const newThreadId = response.data?.threadId;
                if (newThreadId) {
                    setSelectedThreadId(newThreadId);
                    const result = await messagesService.getThreadByJob(jobId, contactId);
                    if (result) {
                        setThread(result.thread);
                        setMessages(result.messages);
                    }
                }
            }
        }
        catch (err) {
            console.error("Failed to send message:", err);
            showError("Failed to send message. Please try again.");
        }
        finally {
            setSending(false);
        }
    };
    const handleNewMessage = () => {
        const textarea = document.querySelector(`.${styles.messageInput}`);
        if (textarea)
            textarea.focus();
    };
    if (!contactId) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.emptyState, children: "Assign a client to this job to view and send messages." }) }));
    }
    if (loading) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: "Loading messages..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: styles.container, children: _jsxs("div", { className: styles.errorState, children: [_jsx("p", { children: error }), _jsx("button", { type: "button", onClick: loadJobConversation, className: styles.retryBtn, children: "Retry" })] }) }));
    }
    const displayMessages = selectedThreadId ? messages : [];
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.header, children: [_jsxs("h2", { className: styles.title, children: ["Messages \u2013 Job #", jobNumber ?? thread?.jobId ?? jobId] }), _jsxs("button", { type: "button", className: styles.newMessageBtn, onClick: handleNewMessage, children: [_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "12", y1: "5", x2: "12", y2: "19" }), _jsx("line", { x1: "5", y1: "12", x2: "19", y2: "12" })] }), "New Message"] })] }), _jsxs("div", { className: styles.messageInputSection, children: [_jsx("textarea", { className: styles.messageInput, placeholder: "Type your message here", value: messageText, onChange: (e) => setMessageText(e.target.value), rows: 4 }), _jsxs("button", { type: "button", className: styles.sendBtn, onClick: handleSendMessage, disabled: !messageText.trim() || sending, children: [_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "22", y1: "2", x2: "11", y2: "13" }), _jsx("polygon", { points: "22 2 15 22 11 13 2 9 22 2" })] }), sending ? "Sending..." : "Send message"] })] }), _jsxs("div", { className: styles.messageHistory, children: [_jsxs("button", { type: "button", className: styles.historyHeader, onClick: () => setIsHistoryCollapsed(!isHistoryCollapsed), children: [_jsx("h3", { className: styles.historyTitle, children: "MESSAGE HISTORY" }), _jsx("svg", { className: `${styles.collapseIcon} ${isHistoryCollapsed ? styles.collapsed : ""}`, width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("polyline", { points: "18 15 12 9 6 15" }) })] }), !isHistoryCollapsed && (_jsx("div", { className: styles.messagesList, children: loadingMessages ? (_jsx("div", { className: styles.loadingState, children: "Loading..." })) : displayMessages.length === 0 ? (_jsx("div", { className: styles.emptyState, children: "No messages yet." })) : ([...displayMessages].reverse().map((msg) => (_jsxs("div", { className: styles.messageItem, children: [_jsxs("div", { className: styles.messageHeader, children: [_jsx("span", { className: styles.senderName, children: msg.senderName }), _jsx("span", { className: styles.timestamp, children: formatRelativeTime(msg.createdAt) })] }), _jsx("div", { className: styles.messageContent, children: msg.content })] }, msg.id)))) }))] }), _jsx("hr", { className: styles.sectionDivider }), _jsxs("div", { className: styles.complaintSection, children: [_jsx("h3", { className: styles.complaintTitle, children: "Complaint" }), complaintNotes || complaintDate ? (_jsxs(_Fragment, { children: [complaintNotes && (_jsx("div", { className: styles.complaintNote, children: complaintNotes })), complaintDate && (_jsx("div", { className: styles.complaintDate, children: formatDateTimeAUS(complaintDate) }))] })) : (_jsx("div", { className: styles.complaintEmpty, children: "No complaint submitted." }))] })] }));
};
